import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../services/graphql';
import CampaignGridCard from '../campaigns/CampaignGridCard';
import { Spinner } from '../common/Spinner';

/**
 * 캠페인 리스트 — admin view of every campaign in the system.
 *
 * Layout:
 *   Stats strip      (전체 / 모집중 / 진행중 / 마감임박 / 이번달 신규)
 *   Status tabs      (전체 / 모집중 / 진행중 / 완료 / 중단)
 *   Filter row       (channel selectbox + search box)
 *   Campaign grid    (CampaignGridCard, 4 per row on lg+)
 *
 * Status mapping to `CampaignStatus` enum:
 *   모집중  → active
 *   진행중  → "active" campaigns whose recruitment period has ended (inferred)
 *   완료    → completed
 *   중단    → paused / cancelled / closed
 */
const CampaignManagementPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<TabKey>('all');
  const [channel, setChannel] = useState<ChannelKey>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // No filters here — admin sees all campaigns; we'll filter client-side.
        const res = await graphqlApi.getCampaigns({});
        if (!cancelled) {
          if (res.success && res.data?.campaigns) {
            setCampaigns(res.data.campaigns);
          } else {
            setError(res.error || 'Failed to load campaigns');
          }
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load campaigns');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ──────────────── derived buckets / stats ──────────────── */

  const now = useMemo(() => new Date(), []);
  const startOfMonth = useMemo(() => {
    const d = new Date(now);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [now]);

  const isRecruiting = (c: any): boolean => {
    if (c.status !== 'active') return false;
    const rp = c.recruitmentPeriod;
    if (!rp) return true;
    if (rp.alwaysRecruiting) return true;
    if (!rp.endDate) return true;
    return new Date(rp.endDate) >= now;
  };

  const isInProgress = (c: any): boolean => {
    // "진행중" = active but recruitment window has already closed → they're
    // actively delivering content.
    if (c.status !== 'active') return false;
    const rp = c.recruitmentPeriod;
    if (!rp || rp.alwaysRecruiting || !rp.endDate) return false;
    return new Date(rp.endDate) < now;
  };

  const isClosingSoon = (c: any): boolean => {
    // 마감임박: recruiting and within 7 days of end
    if (!isRecruiting(c)) return false;
    const end = c.recruitmentPeriod?.endDate;
    if (!end) return false;
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 7;
  };

  const isStopped = (c: any): boolean =>
    c.status === 'paused' || c.status === 'cancelled' || c.status === 'closed';

  const isCompleted = (c: any): boolean => c.status === 'completed';

  const stats = useMemo(() => {
    const total = campaigns.length;
    const recruiting = campaigns.filter(isRecruiting).length;
    const inProgress = campaigns.filter(isInProgress).length;
    const closingSoon = campaigns.filter(isClosingSoon).length;
    const monthlyNew = campaigns.filter((c) => {
      if (!c.createdAt) return false;
      return new Date(c.createdAt) >= startOfMonth;
    }).length;
    return { total, recruiting, inProgress, closingSoon, monthlyNew };
  }, [campaigns, startOfMonth]);

  /* ──────────────── filtering ──────────────── */

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return campaigns.filter((c) => {
      // Tab
      if (tab === 'recruiting' && !isRecruiting(c)) return false;
      if (tab === 'inProgress' && !isInProgress(c)) return false;
      if (tab === 'completed' && !isCompleted(c)) return false;
      if (tab === 'stopped' && !isStopped(c)) return false;
      // tab === 'all' → no filter

      // Channel
      if (channel !== 'all') {
        const channels: string[] = Array.isArray(c.contentChannels) && c.contentChannels.length > 0
          ? c.contentChannels
          : c.contentChannel
            ? [c.contentChannel]
            : [];
        if (!channels.includes(channel)) return false;
      }

      // Search — campaign name or brand name
      if (q) {
        const inName = (c.campaignName || '').toLowerCase().includes(q);
        const inBrand = (c.brandName || '').toLowerCase().includes(q);
        if (!inName && !inBrand) return false;
      }

      return true;
    });
  }, [campaigns, tab, channel, searchTerm]);

  /* ──────────────── render ──────────────── */

  const tabs: Array<{ key: TabKey; labelKey: string; defaultLabel: string; badge?: number }> = [
    { key: 'all', labelKey: 'admin:campaigns.tabs.all', defaultLabel: '전체', badge: stats.total },
    { key: 'recruiting', labelKey: 'admin:campaigns.tabs.recruiting', defaultLabel: '모집중', badge: stats.recruiting },
    { key: 'inProgress', labelKey: 'admin:campaigns.tabs.inProgress', defaultLabel: '진행중', badge: stats.inProgress },
    { key: 'completed', labelKey: 'admin:campaigns.tabs.completed', defaultLabel: '완료' },
    { key: 'stopped', labelKey: 'admin:campaigns.tabs.stopped', defaultLabel: '중단' },
  ];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('admin:sidebar.activityManagement')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{t('admin:sidebar.campaignList')}</span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin:sidebar.campaignList')}</h1>

        {/* Stats strip */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t('admin:campaigns.stats.total', '전체캠페인')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:campaigns.stats.recruiting', '모집중')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:campaigns.stats.inProgress', '진행중')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:campaigns.stats.closingSoon', '마감임박(7일)')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('admin:campaigns.stats.monthlyNew', '이번달 신규')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-100">
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : stats.total.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : stats.recruiting.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : stats.inProgress.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : stats.closingSoon.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-lg font-bold text-gray-900">
                  {loading ? '—' : stats.monthlyNew.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-1 border-b border-gray-200">
          {tabs.map((tb) => {
            const active = tb.key === tab;
            return (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t(tb.labelKey, tb.defaultLabel)}
                {typeof tb.badge === 'number' && (
                  <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                    active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tb.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filter row */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as ChannelKey)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
          >
            <option value="all">{t('admin:campaigns.filter.allChannels', '전체 채널')}</option>
            <option value="instagram">{t('admin:campaigns.filter.instagram', '인스타그램')}</option>
            <option value="blog">{t('admin:campaigns.filter.blog', '블로그')}</option>
            <option value="youtube">{t('admin:campaigns.filter.youtube', '유튜브')}</option>
          </select>

          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('admin:campaigns.filter.searchPlaceholder', '캠페인명, 브랜드명 검색')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Result count */}
        <div className="text-sm text-gray-500">
          {loading
            ? t('common:loading', '불러오는 중...')
            : t('admin:campaigns.totalCount', { count: filtered.length, defaultValue: '전체 {{count}}개' })}
        </div>

        {/* Campaign grid — 4 per row on lg+ */}
        {loading ? (
          <div className="py-16 flex justify-center">
            <Spinner size="medium" />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            {t('admin:campaigns.empty', '조건에 맞는 캠페인이 없습니다.')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((c, idx) => (
              <CampaignGridCard
                key={c.campaignId}
                campaign={c}
                index={idx}
                linkPrefix="/campaigns"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

type TabKey = 'all' | 'recruiting' | 'inProgress' | 'completed' | 'stopped';
type ChannelKey = 'all' | 'instagram' | 'blog' | 'youtube';

export default CampaignManagementPage;
